import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface LoginCredentials {
  email: string;
  password: string;
  code?: string;
}

export interface LoginResponse {
  token?: string;
  twofa_required?: boolean;
}

export interface UserData {
  id: string;
  organization_id: string;
  first_name: string;
  last_name: string;
  email: string;
  personality: string;
  tone_style?: string;
  twofa_enabled: boolean;
  weekly_schedule?: WeeklyScheduleEntry[] | null;
  slot_step_minutes?: number;
  sex?: UserSex;
  offensive_limit?: number | null;
  reminder_hours?: number | null;
  max_visits_per_day?: number | null;
  created_at: string;
  updated_at: string;
}

export interface WeeklyScheduleEntry {
  day: number; // 0 = niedziela
  working: boolean;
  start?: string; // "09:00"
  end?: string; // "18:00"
}

/** Both halves are optional: an omitted one is left as it was stored. */
export interface UpdateWeeklyScheduleRequest {
  schedule?: WeeklyScheduleEntry[];
  slot_step_minutes?: number;
}

/** Mirrors SlotStepMinutes on the backend — anything else is rejected with a 422. */
export const SLOT_STEP_OPTIONS = [15, 20, 30, 45, 60, 90, 120] as const;

export const DEFAULT_SLOT_STEP_MINUTES = 60;

/** Mirrors UserSex on the backend — anything else is rejected with a 422. */
export type UserSex = 'female' | 'male';

export const SEX_OPTIONS: UserSex[] = ['female', 'male'];

export const DEFAULT_USER_SEX: UserSex = 'female';

/** Two weeks, mirroring MAX_REMINDER_HOURS on the API. */
export const MAX_REMINDER_HOURS = 336;

/** Hours before a visit that a reminder goes out when the user sets nothing. */
export const DEFAULT_REMINDER_HOURS = 24;

/** Visits one client may book on a single day when the user sets nothing. */
export const DEFAULT_MAX_VISITS_PER_DAY = 1;

/** Mirrors MAX_VISITS_PER_DAY_CEILING on the API. */
export const MAX_VISITS_PER_DAY = 24;

/**
 * Every field is optional and an omitted one is left as it was stored, so each card in
 * the settings screen sends only what it shows. Null is a value of its own on the
 * nullable fields: it clears the override and restores the deployment default.
 */
export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  personality?: string;
  tone_style?: string | null;
  sex?: UserSex;
  offensive_limit?: number | null;
  reminder_hours?: number | null;
  max_visits_per_day?: number | null;
}

export interface ResetPasswordRequest {
  email: string;
}

export interface RegisterRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  organization_name: string;
}

export interface ActivateRequest {
  email: string;
  code: string;
}

export interface CompleteSetupRequest {
  setup_token: string;
  first_name: string;
  last_name: string;
  organization_name: string;
}

export interface GoogleCalendarAuthResponse {
  redirect_url: string;
}

export interface GoogleCalendarStatusResponse {
  connected: boolean;
}

export interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai' | 'customer';
  timestamp: string;
}

// API Response for Messages
export interface MessageData {
  id: string;
  thread_id: string;
  user_id: string | null;
  client_id: string | null;
  message: string;
  read_at: string | null;
  is_ai: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user: ThreadUser | null;
  client: ThreadClient | null;
}

export interface MessagesApiResponse {
  data: MessageData[];
  metadata: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_count: number;
  };
}

export interface MessagesResponse {
  messages: Message[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export interface MessagesParams {
  page?: number;
  per_page?: number;
}

// API Response Types
export interface ThreadUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface ThreadClient {
  id: string;
  name: string;
  client_external_id: string;
}

export interface ThreadData {
  id: string;
  organization_id: string;
  user_id: string;
  client_id: string;
  state: string;
  taken_until: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user: ThreadUser;
  client: ThreadClient;
}

export interface ThreadsMetadata {
  current_page: number;
  per_page: number;
  total_pages: number;
  total_count: number;
}

export interface ThreadsApiResponse {
  data: ThreadData[];
  metadata: ThreadsMetadata;
}

// UI Types
export interface Thread {
  id: string;
  name: string;
  status: 'new' | 'active' | 'needs-support' | 'done';
  lastMessage?: string;
  messages: Message[];
  // Additional fields from API
  organizationId: string;
  userId: string;
  clientId: string;
  takenUntil: string | null;
  createdAt: string;
  updatedAt: string;
  user: ThreadUser;
  client: ThreadClient;
}

export interface ThreadsResponse {
  threads: Thread[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export interface ThreadsParams {
  page?: number;
  per_page?: number;
  archive?: boolean;
}

// Helper function to map API response to UI format
const mapThreadDataToThread = (data: ThreadData): Thread => {
  return {
    id: data.id,
    name: data.client.name,
    status: data.state?.toUpperCase() === 'DONE' ? 'done' : 'active',
    lastMessage: undefined, // Will be populated from messages if available
    messages: [], // Will be loaded separately
    organizationId: data.organization_id,
    userId: data.user_id,
    clientId: data.client_id,
    takenUntil: data.taken_until,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    user: data.user,
    client: data.client,
  };
};

// Helper function to map message data to UI format
const mapMessageDataToMessage = (data: MessageData): Message => {
  // Determine sender type based on is_ai, client_id, and user_id
  let sender: 'user' | 'ai' | 'customer';
  if (data.is_ai) {
    sender = 'ai';
  } else if (data.client_id) {
    sender = 'customer';
  } else {
    sender = 'user';
  }

  return {
    id: data.id,
    text: data.message,
    sender: sender,
    timestamp: data.created_at,
  };
};

export interface AuthResponse {
  token: string;
  setup_token?: string;
  requires_setup?: boolean;
}

export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth', credentials);
    return response.data;
  },

  getMe: async (): Promise<UserData> => {
    const response = await api.get<UserData>('/me');
    return response.data;
  },

  updateProfile: async (data: UpdateProfileRequest): Promise<UserData> => {
    const response = await api.patch<UserData>('/me', data);
    return response.data;
  },

  updateWeeklySchedule: async (data: UpdateWeeklyScheduleRequest): Promise<UserData> => {
    const response = await api.put<UserData>('/me/schedule', data);
    return response.data;
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    await api.post('/auth/reset-password', data);
  },

  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    return response.data;
  },

  activate: async (data: ActivateRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/activate', data);
    return response.data;
  },

  completeSetup: async (data: CompleteSetupRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/complete-setup', data);
    return response.data;
  },

  loginWithGoogle: async (idToken: string): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/google', { id_token: idToken });
    return response.data;
  },

  loginWithApple: async (
    idToken: string,
    firstName: string,
    lastName: string,
  ): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/apple', {
      id_token: idToken,
      first_name: firstName,
      last_name: lastName,
    });
    return response.data;
  },

  googleLogin: async (): Promise<{ redirect_url: string }> => {
    const response = await api.get<{ redirect_url: string }>('/auth/google');
    return response.data;
  },

  appleLogin: async (): Promise<{ redirect_url: string }> => {
    const response = await api.get<{ redirect_url: string }>('/auth/apple');
    return response.data;
  },

  changePassword: async (data: {
    current_password: string;
    new_password: string;
    confirm_new_password: string;
  }): Promise<void> => {
    await api.patch('/me/password', data);
  },

  get2faSecret: async (): Promise<{ secret: string; otpauth_url: string }> => {
    const response = await api.get<{ secret: string; otpauth_url: string }>('/me/2fa/secret');
    return response.data;
  },

  enable2fa: async (code: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/me/2fa/enable', { code });
    return response.data;
  },

  disable2fa: async (code: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/me/2fa/disable', { code });
    return response.data;
  },
};

export const googleCalendarAPI = {
  getAuthUrl: async (): Promise<GoogleCalendarAuthResponse> => {
    const response = await api.get<GoogleCalendarAuthResponse>('/google-calendar/auth');
    return response.data;
  },

  handleCallback: async (code: string, state: string): Promise<void> => {
    await api.post('/google-calendar/callback', {
      code,
      state,
    });
  },

  getStatus: async (): Promise<GoogleCalendarStatusResponse> => {
    const response = await api.get<GoogleCalendarStatusResponse>('/google-calendar/status');
    return response.data;
  },

  disconnect: async (): Promise<void> => {
    await api.delete('/google-calendar/disconnect');
  },
};

export interface Source {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export const sourcesAPI = {
  getAll: async (): Promise<Source[]> => {
    const response = await api.get<Source[]>('/sources');
    return response.data;
  },
};

export interface MessengerConnectedPage {
  id: string;
  page_id: string;
  page_name: string;
  source_id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

export interface MessengerAuthResponse {
  redirect_url: string;
}

export interface MessengerPage {
  id: string;
  name: string;
  access_token: string;
}

export interface MessengerCallbackData {
  pages: MessengerPage[];
  organization_id: string;
  source_id: string;
}

export interface MessengerConnectRequest {
  page_id: string;
  page_name: string;
  page_access_token: string;
  organization_id: string;
  source_id: string;
}

export interface MessengerConnectResponse {
  success: boolean;
  page_id: string;
  page_name: string;
}

export const messengerAPI = {
  getAuthUrl: async (organizationId: string, sourceId: string): Promise<MessengerAuthResponse> => {
    const response = await api.get<MessengerAuthResponse>('/messenger/auth', {
      params: { organization_id: organizationId, source_id: sourceId },
    });
    return response.data;
  },

  connectPage: async (data: MessengerConnectRequest): Promise<MessengerConnectResponse> => {
    const response = await api.post<MessengerConnectResponse>('/messenger/auth/connect', data);
    return response.data;
  },

  getPages: async (organizationId: string): Promise<MessengerConnectedPage[]> => {
    const response = await api.get<MessengerConnectedPage[]>(`/messenger/pages/${organizationId}`);
    return response.data;
  },

  disconnect: async (organizationId: string): Promise<void> => {
    await api.delete('/messenger/disconnect', { data: { organization_id: organizationId } });
  },
};

export const threadsAPI = {
  getThreads: async (params: ThreadsParams = {}): Promise<ThreadsResponse> => {
    const { page = 1, per_page = 10, archive = false } = params;
    const response = await api.get<ThreadsApiResponse>('/threads', {
      params: { page, per_page, archive },
    });

    const { data, metadata } = response.data;

    return {
      threads: data.map(mapThreadDataToThread),
      total: metadata.total_count,
      page: metadata.current_page,
      per_page: metadata.per_page,
      has_more: metadata.current_page < metadata.total_pages,
    };
  },

  getThread: async (threadId: string): Promise<Thread> => {
    const response = await api.get<ThreadData>(`/threads/${threadId}`);
    return mapThreadDataToThread(response.data);
  },

  getMessages: async (threadId: string, params: MessagesParams = {}): Promise<MessagesResponse> => {
    const { page = 1, per_page = 10 } = params;
    const response = await api.get<MessagesApiResponse>(`/messages/${threadId}`, {
      params: { page, per_page },
    });

    const { data, metadata } = response.data;

    return {
      messages: data.map(mapMessageDataToMessage),
      total: metadata.total_count,
      page: metadata.current_page,
      per_page: metadata.per_page,
      has_more: metadata.current_page < metadata.total_pages,
    };
  },

  takeOver: async (threadId: string): Promise<ThreadData> => {
    const response = await api.post<{ data: ThreadData }>(`/threads/${threadId}/take-over`);
    return response.data.data;
  },

  release: async (threadId: string): Promise<ThreadData> => {
    const response = await api.post<{ data: ThreadData }>(`/threads/${threadId}/release`);
    return response.data.data;
  },

  sendMessage: async (threadId: string, message: string): Promise<MessageData> => {
    const response = await api.post<{ data: MessageData }>(`/messages/${threadId}`, {
      message,
    });
    return response.data.data;
  },
};

// Service types
export interface Service {
  id: string;
  organization_id: string;
  name: string;
  price_from: number | null;
  price_to: number | null;
  time: number;
  created_at: string;
  updated_at: string;
}

export interface ServiceCreate {
  name: string;
  price_from?: number | null;
  price_to?: number | null;
  time?: number;
}

export interface ServiceUpdate {
  name?: string;
  price_from?: number | null;
  price_to?: number | null;
  time?: number;
}

export interface ServicesApiResponse {
  data: Service[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ServicesResponse {
  services: Service[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export interface ServicesParams {
  page?: number;
  per_page?: number;
}

export const servicesAPI = {
  getAll: async (params: ServicesParams = {}): Promise<ServicesResponse> => {
    const { page = 1, per_page = 10 } = params;
    const response = await api.get<ServicesApiResponse>('/services', {
      params: { page, per_page },
    });

    const {
      data,
      total_count,
      page: responsePage,
      per_page: responsePerPage,
      total_pages,
    } = response.data;

    return {
      services: data,
      total: total_count,
      page: responsePage,
      per_page: responsePerPage,
      has_more: responsePage < total_pages,
    };
  },

  getById: async (id: string): Promise<Service> => {
    const response = await api.get<Service>(`/services/${id}`);
    return response.data;
  },

  create: async (data: ServiceCreate): Promise<Service> => {
    const response = await api.post<Service>('/services', data);
    return response.data;
  },

  update: async (id: string, data: ServiceUpdate): Promise<Service> => {
    const response = await api.put<Service>(`/services/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/services/${id}`);
  },
};

export interface Client {
  id: string;
  name: string;
  organization_id: string;
  source_id: string;
  confirmed: boolean;
  /** Offensive strikes recorded by the assistant; reaching the limit blocks the client. */
  offensive: number;
  data: { phone_number?: string; email?: string };
  source?: { id: string; name: string };
  created_at: string;
  updated_at: string;
}

export const clientsAPI = {
  getAll: (page = 1, perPage = 10, search?: string) =>
    api.get<{ data: Client[]; total: number }>('/clients', {
      params: { page, per_page: perPage, ...(search ? { search } : {}) },
    }),
  getById: (id: string) => api.get<Client>(`/clients/${id}`),
  resetOffensive: (id: string) => api.post<Client>(`/clients/${id}/reset-offensive`),
};

export interface Visit {
  id: string;
  organization_id: string;
  user_id: string;
  client_id: string;
  visit_at: string;
  client?: { id: string; name: string; data?: { phone_number?: string; email?: string } };
  /** One appointment can cover several services running back to back. */
  services?: { id: string; name: string; time: number }[];
  /** Their combined length — how long the slot is blocked for. */
  duration_minutes?: number;
  user?: { id: string; first_name: string; last_name: string };
  created_at: string;
  updated_at: string;
}

export interface VisitsFilters {
  search?: string;
  date_from?: string;
  date_to?: string;
}

export interface DailyAvailability {
  date: string; // YYYY-MM-DD
  times: string[]; // HH:mm
}

export const visitsAPI = {
  getAll: (page = 1, perPage = 10, filters: VisitsFilters = {}) =>
    api.get<{ data: Visit[]; total: number }>('/visits', {
      params: { page, per_page: perPage, ...filters },
    }),
  getById: (id: string) => api.get<Visit>(`/visits/${id}`),
  // Free slots of the staff member assigned to the visit, for the given day(s).
  getAvailability: (id: string, from: string, to?: string) =>
    api.get<{ time: DailyAvailability[] }>(`/visits/${id}/availability`, {
      params: { from, ...(to ? { to } : {}) },
    }),
  reschedule: (id: string, visitAt: string) =>
    api.put<Visit>(`/visits/${id}`, { visit_at: visitAt }),
  cancel: (id: string) => api.delete(`/visits/${id}`),
};

export const pushAPI = {
  subscribe: async (data: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  }): Promise<void> => {
    await api.post('/push-subscriptions', data);
  },
};

export default api;
